/**
 * AppProvider Component
 *
 * This module provides global theme and layout management for the application through React Context.
 * It handles theme switching (light/dark mode), sidebar toggling, and responsive layout adjustments.
 *
 * Key Features:
 * - Theme state management (light/dark mode)
 * - Sidebar visibility and style control
 * - Responsive layout handling
 * - Local storage persistence
 * - HTML attribute synchronization
 */
import {createContext, useContext, useEffect, useState} from 'react';

/**
 * Theme configuration type definition
 * Defines all customizable aspects of the application theme and layout
 */
type ThemeState = {
  lang: string; // Language setting (e.g. 'en')
  dir: string; // Text direction (e.g. 'ltr', 'rtl')
  class: string; // Theme class ('light' or 'dark')
  dataMenuStyles: string; // Menu styling
  dataNavLayout: string; // Navigation layout type
  dataHeaderStyles: string; // Header styling
  dataVerticalStyle: string; // Vertical sidebar style
  toggled: string; // Sidebar toggle state
  dataNavStyle: string; // Navigation interaction style
  horStyle: string; // Horizontal layout style
  dataPageStyle: string; // Page layout style
  dataWidth: string; // Content width setting
  dataMenuPosition: string; // Menu position
  dataHeaderPosition: string; // Header position
  loader: string; // Loader state
  iconOverlay: string; // Icon overlay setting
  colorPrimaryRgb: string; // Primary color in RGB
  colorPrimary: string; // Primary color
  bodyBg: string; // Body background
  Light: string; // Light theme settings
  darkBg: string; // Dark background
  inputBorder: string; // Input border style
  bgImg: string; // Background image
  iconText: string; // Icon text style
};

/**
 * Context type definition
 * Defines the shape of the context data and available methods
 */
type AppContextT = {
  theme: ThemeState;
  updateTheme: (updates: Partial<ThemeState>) => void;
  toggleDarkMode: () => void;
  toggleSidebar: () => void;
};

/**
 * Default theme configuration
 */
const defaultTheme: ThemeState = {
  lang: 'en',
  dir: 'ltr',
  class: 'dark',
  dataMenuStyles: 'dark',
  dataNavLayout: 'vertical',
  dataHeaderStyles: 'dark',
  dataVerticalStyle: 'overlay',
  toggled: 'close',
  dataNavStyle: '',
  horStyle: '',
  dataPageStyle: 'regular',
  dataWidth: 'fullwidth',
  dataMenuPosition: 'fixed',
  dataHeaderPosition: 'fixed',
  loader: 'disable',
  iconOverlay: '',
  colorPrimaryRgb: '',
  colorPrimary: '',
  bodyBg: '',
  Light: '',
  darkBg: '',
  inputBorder: '',
  bgImg: '',
  iconText: '',
};

// Create context with empty default value
const AppContext = createContext({} as AppContextT);

/**
 * AppProvider Component
 * Manages global theme state and provides theme-related utilities to children
 */
export const AppProvider = ({children}: {children: React.ReactNode}) => {
  const [theme, setTheme] = useState<ThemeState>(defaultTheme);

  // Add this effect right after the AppProvider component definition
  useEffect(() => {
    const initializeTheme = () => {
      if (window.innerWidth <= 992) {
        updateTheme({toggled: 'close'});
        document.documentElement.setAttribute('data-toggled', 'close');
      }
    };

    initializeTheme();
  }, []);

  /**
   * Updates theme state with partial updates
   */
  const updateTheme = (updates: Partial<ThemeState>) => {
    setTheme(prev => ({...prev, ...updates}));
  };

  /**
   * Synchronizes theme state with HTML document attributes
   */
  const updateHtmlAttributes = (themeState: ThemeState) => {
    const html = document.documentElement;

    // Set core layout attributes
    html.setAttribute('data-vertical-style', themeState.dataVerticalStyle);
    html.setAttribute('data-nav-style', themeState.dataNavStyle);
    html.setAttribute('data-toggled', themeState.toggled);

    // Set theme attributes
    html.setAttribute('class', themeState.class);
    html.setAttribute('data-menu-styles', themeState.dataMenuStyles);
    html.setAttribute('data-nav-layout', themeState.dataNavLayout);
  };

  /**
   * Toggles between light and dark mode
   * Updates theme state and local storage accordingly
   */
  const toggleDarkMode = () => {
    // Do nothing - we're always in dark mode
  };

  /**
   * Handles sidebar toggling for both desktop and mobile views
   * Manages different sidebar styles and responsive behavior
   */
  const toggleSidebar = () => {
    const html = document.documentElement;

    if (window.innerWidth >= 992) {
      // Desktop view logic
      if (theme.dataNavLayout === 'vertical') {
        const verticalStyle = theme.dataVerticalStyle;

        switch (verticalStyle) {
          case 'closed': {
            const newClosedToggled = theme.toggled === 'close-menu-close' ? '' : 'close-menu-close';
            updateTheme({
              toggled: newClosedToggled,
              dataNavStyle: '',
            });
            html.setAttribute('data-toggled', newClosedToggled);
            break;
          }

          case 'overlay': {
            const overlayToggled = theme.toggled === 'icon-overlay-close' ? '' : 'icon-overlay-close';
            updateTheme({
              toggled: overlayToggled,
              dataNavStyle: '',
            });
            html.setAttribute('data-toggled', overlayToggled);
            break;
          }

          case 'icontext': {
            const icontextToggled = theme.toggled === 'icon-text-close' ? '' : 'icon-text-close';
            updateTheme({
              toggled: icontextToggled,
              dataNavStyle: '',
            });
            html.setAttribute('data-toggled', icontextToggled);
            break;
          }
        }

        // Handle navigation styles
        if (theme.dataNavStyle) {
          const navStyleMap: {[key: string]: string} = {
            'menu-click': 'menu-click-closed',
            'menu-hover': 'menu-hover-closed',
            'icon-click': 'icon-click-closed',
            'icon-hover': 'icon-hover-closed',
          };

          if (theme.dataNavStyle in navStyleMap) {
            const newToggled = theme.toggled === navStyleMap[theme.dataNavStyle] ? '' : navStyleMap[theme.dataNavStyle];
            updateTheme({toggled: newToggled});
            html.setAttribute('data-toggled', newToggled);
          }
        }
      }
    } else {
      // Mobile view logic
      const newToggled = theme.toggled === 'close' ? 'open' : 'close';
      updateTheme({toggled: newToggled});
      html.setAttribute('data-toggled', newToggled);

      // Handle overlay for mobile
      setTimeout(() => {
        const overlay = document.querySelector('#responsive-overlay');
        if (overlay && newToggled === 'open') {
          overlay.classList.add('active');
        }
      }, 100);
    }
  };

  // Sync HTML attributes when theme changes
  useEffect(() => {
    updateHtmlAttributes(theme);
  }, [theme]);

  // Handle responsive behavior
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 992) {
        updateTheme({toggled: 'close'});
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Initialize theme from localStorage
  useEffect(() => {
    const storedTheme = localStorage.getItem('appTheme');
    if (storedTheme) {
      setTheme(JSON.parse(storedTheme));
    }

    // Apply dark theme if previously selected
    if (localStorage.getItem('ynexdarktheme')) {
      updateTheme({class: 'dark', dataHeaderStyles: 'dark', dataMenuStyles: 'dark'});
    }
  }, []);

  // Persist theme changes to localStorage
  useEffect(() => {
    localStorage.setItem('appTheme', JSON.stringify(theme));
  }, [theme]);

  // Manage sidebar toggle state
  useEffect(() => {
    const html = document.documentElement;

    if (theme.toggled === 'close-menu-close' || theme.toggled === 'close') {
      html.setAttribute('data-sidebar-toggle', 'closed');
    } else {
      html.removeAttribute('data-sidebar-toggle');
    }
  }, [theme.toggled]);

  // Modified initialization effect to always set dark theme
  useEffect(() => {
    localStorage.setItem('ynexdarktheme', 'dark');
    localStorage.removeItem('ynexlighttheme');

    updateTheme({
      class: 'dark',
      dataHeaderStyles: 'dark',
      dataMenuStyles: 'dark',
    });
  }, []);

  return (
    <AppContext.Provider
      value={{
        theme,
        updateTheme,
        toggleDarkMode,
        toggleSidebar,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

/**
 * Custom hook to access the AppContext
 * @throws Error if used outside of AppProvider
 */
export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within a AppProvider');
  }
  return context;
};
